import React, { Fragment, useMemo } from 'react';
import styles from './schedule.module.scss';
import ScheduleItem from './scheduleItem/schedule-item';
import EmptySchedule from '../emptySchedule/emptySchedule';
import useListSeasons from '../../customHooks/useListSeasons';
import moment from 'moment';
import TournamentsItem from './tournamentItem/tournament-item';
import { ITournaments } from '../../graphql-types/tournaments';

interface ScheduleData extends ITournaments {
  date: string | Date;
  id: string;
  homeTeam: {
    id: string;
    name: string;
  };
  contextualInfo: {
    homeTeam: string;
    opponent: {
      id: string;
      name: string;
      logo: string | null;
    };
  };
  startDateTime: {
    dateTimeString: string | Date;
    noScheduledTime: boolean;
    timeZoneName: string;
  };
}

interface ScheduleProps {
  scheduleData: ScheduleData[];
  tournamentsData: ITournaments[];
}

const Schedule: React.FC<ScheduleProps> = ({ scheduleData, tournamentsData }) => {
  const { isActiveSeason, seasonYear } = useListSeasons();
  /* Sort the array by date */
  const sortedData = scheduleData?.slice().sort((a, b) => {
    let bDate = new Date(b.startDateTime.dateTimeString).valueOf();
    let aDate = new Date(a.startDateTime.dateTimeString).valueOf();

    if (bDate > aDate) {
      return -1;
    }

    if (bDate < aDate) {
      return 1;
    }

    return 0;
  });

  /* Split data into completed and upcoming arrays */
  const now = new Date().valueOf();
  let completedList = [];
  let upcomingList = [];

  /* Find the first date after now */
  for (let i = 0; i < sortedData?.length; i++) {
    if (new Date(sortedData[i].startDateTime.dateTimeString).valueOf() < now) {
      completedList.push(sortedData[i]);
    } else {
      upcomingList.push(sortedData[i]);
    }
  }

  const completedTournaments = useMemo(
    () =>
      tournamentsData
        ?.map(data => ({
          ...data,
          date: new Date(data.startDate).toISOString()
        }))
        .filter(({ endDate }) => {
          const todaysDate = moment();
          const tournamentsEndDate = moment(endDate);
          // Set start of August (start of the season )
          const startOfSeason = moment([Number(seasonYear), 7, 1]);
          // Set end of July (end of the season)
          const endOfSeason = moment([Number(seasonYear) + 1, 5, 30]).endOf('month');

          return (
            seasonYear &&
            tournamentsEndDate < todaysDate &&
            tournamentsEndDate.year() >= Number(seasonYear) &&
            tournamentsEndDate >= startOfSeason &&
            tournamentsEndDate <= endOfSeason
          );
        }),
    [seasonYear, tournamentsData]
  );

  const upcomingTournaments = useMemo(
    () =>
      tournamentsData
        ?.map(tournamentData => ({
          ...tournamentData,
          date: new Date(tournamentData.startDate).toISOString()
        }))
        .filter(({ endDate }) => moment(endDate) >= moment()),
    [tournamentsData]
  );

  const completedListFormatted = completedList.map((completed: any) => ({
    ...completed,
    date: new Date(completed.startDateTime?.dateTimeString)
  }));

  const upcomingListFormatted = upcomingList.map((upcoming: any) => ({
    ...upcoming,
    date: new Date(upcoming.startDateTime?.dateTimeString)
  }));

  const completedCombined = useMemo(
    () =>
      completedListFormatted.concat(completedTournaments).sort((a, b) => {
        const first: any = new Date(a.date);
        const second: any = new Date(b.date);

        return first - second;
      }),
    [completedListFormatted, completedTournaments]
  );

  const upcomingCombined = useMemo(
    () =>
      upcomingListFormatted.concat(upcomingTournaments).sort((a, b) => {
        const first: any = new Date(a.date);
        const second: any = new Date(b.date);

        return first - second;
      }),
    [upcomingListFormatted, upcomingTournaments]
  );

  const CompletedItems =
    completedCombined.length > 0 ? (
      completedCombined.map((obj, index: number) => (
        <Fragment key={index}>
          {obj?.__typename === 'DualMatch' && <ScheduleItem data={obj} type="completed" />}
          {obj?.__typename === 'TournamentPublic' && <TournamentsItem data={obj} />}
        </Fragment>
      ))
    ) : (
      <EmptySchedule
        type="completed"
        title="No completed matches found"
        copy="Completed matches will appear here"
      />
    );

  const UpcomingItems =
    upcomingCombined.length > 0 ? (
      upcomingCombined.map((obj, index: number) => (
        <Fragment key={index}>
          {obj?.__typename === 'DualMatch' && <ScheduleItem data={obj} type="upcoming" />}
          {obj?.__typename === 'TournamentPublic' && <TournamentsItem data={obj} />}
        </Fragment>
      ))
    ) : (
      <EmptySchedule
        type="upcoming"
        title="No upcoming matches found"
        copy="Upcoming matches will appear here"
      />
    );

  return (
    <div className={styles.schedule}>
      <h2>Schedule</h2>
      <h3>Completed</h3>
      <div className={`${styles.box} ${completedList.length === 0 && styles.empty}`}>
        {CompletedItems}
      </div>
      {isActiveSeason && (
        <>
          <h3>Upcoming</h3>
          <div className={`${styles.box} ${upcomingList.length === 0 && styles.empty}`}>
            {UpcomingItems}
          </div>
        </>
      )}
    </div>
  );
};

export default Schedule;
